import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import TeesSubNav from '../components/TeesSubNav';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category labels
const CATEGORY_LABELS = {
  'short-sleeve': 'Short Sleeve',
  'long-sleeve': 'Long Sleeve',
  'new-designs': 'New Designs',
  'caps': 'Headwear',
  'hoodies': 'Hoodies',
  'tank-tops': 'Tank Tops'
};

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('tees') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function TeesProduct() {
  const { category, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { addItem, itemCount, openCart } = useCart();

  const categoryLabel = CATEGORY_LABELS[category] || category;

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  // If switching to a size with less stock than the currently selected
  // quantity, pull the quantity back down so it can't exceed what's on hand.
  useEffect(() => {
    if (!product) return;
    const stock = (product.stock_by_size || {})[selectedSize];
    if (stock != null && quantity > stock) {
      setQuantity(Math.max(1, stock));
    }
  }, [selectedSize, product]);

  const fetchProduct = async () => {
    try {
      // Get all products in category and find the one we need
      const response = await axios.get(`${BACKEND_URL}/api/tees/category/${category}`);
      const found = response.data.find(p => p.id === productId);
      if (found) {
        setProduct(found);
      } else {
        toast.error('Product not found');
        navigate(`/shop/tees/${category}`);
      }
    } catch (error) {
      toast.error('Failed to load product');
      navigate(`/shop/tees/${category}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="substack-site">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const sizes = product.sizes || ['S', 'M', 'L', 'XL', '2XL', '3XL'];
  const stockBySize = product.stock_by_size || {};
  const isSizeOutOfStock = (size) => stockBySize[size] === 0;
  const selectedOutOfStock = isSizeOutOfStock(selectedSize);
  const availableForSelectedSize = stockBySize[selectedSize];
  // Missing/null stock for a size means unlimited — keep the existing 1-5
  // picker in that case; otherwise cap options at whatever's actually left.
  const maxQty = availableForSelectedSize == null ? 5 : Math.max(1, Math.min(5, availableForSelectedSize));
  const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1);
  const basePrice = product.base_price || product.price || 28;
  const price2xl = product.price_2xl || basePrice + 1.50;
  const price3xl = product.price_3xl || basePrice + 2.50;
  const onSale = product.on_sale && product.sale_price != null;

  // Calculate price based on size (flat sale_price applies to every size when on sale)
  const getOriginalPrice = (size) => {
    if (size === '3XL') return price3xl;
    if (size === '2XL') return price2xl;
    return basePrice;
  };
  const getPrice = (size) => onSale ? product.sale_price : getOriginalPrice(size);

  const currentPrice = getPrice(selectedSize);
  const features = product.features || [];
  const colors = product.colors || [];
  const chartRows = (product.size_chart || []).filter(r => r.label);
  const chartSizes = sizes.filter(s => chartRows.some(r => r.values?.[s]));
  const needsColor = colors.length > 0 && !selectedColor;
  const tieredPricing = price2xl !== basePrice || price3xl !== basePrice;
  const allImages = [product.image_url, ...(product.additional_images || [])].filter(Boolean);

  const handleAddToCart = () => {
    addItem({
      shop: 'tees',
      product_id: product.id,
      product_title: product.title,
      image_url: product.image_url,
      variant: selectedSize,
      color: selectedColor || undefined,
      variant_label: selectedColor ? `${selectedColor} · Size ${selectedSize}` : `Size ${selectedSize}`,
      price: currentPrice,
      quantity: quantity,
      max_quantity: availableForSelectedSize,
      special_instructions: null
    });
    toast.success(`${product.title} added to cart!`);
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      <TeesSubNav />

      {/* Breadcrumb */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 20px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <Link 
            to="/shop/tees" 
            style={{ color: '#666', textDecoration: 'none' }}
          >
            Beach Bum Tees
          </Link>
          <span style={{ color: '#ccc' }}>/</span>
          <Link 
            to={`/shop/tees/${category}`} 
            style={{ color: '#666', textDecoration: 'none' }}
          >
            {categoryLabel}
          </Link>
          <span style={{ color: '#ccc' }}>/</span>
          <span style={{ color: '#1a1a1a' }}>{product.title}</span>
        </div>
      </div>

      {/* Main Content */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '30px 20px 60px' 
      }}>
        <div 
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '50px',
            alignItems: 'start'
          }}
          data-testid={`detail-${product.id}`}
        >
          {/* Product Image / Gallery */}
          <div style={{ position: 'sticky', top: '20px' }}>
            <div style={{ position: 'relative', marginBottom: allImages.length > 1 ? '12px' : 0 }}>
              <img
                src={allImages[selectedImageIndex]}
                alt={product.title}
                style={{ width: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'block' }}
              />
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)}
                    style={{
                      position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%',
                      width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                    }}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => setSelectedImageIndex((prev) => (prev + 1) % allImages.length)}
                    style={{
                      position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%',
                      width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                    }}
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            {allImages.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {allImages.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    style={{
                      padding: 0, border: idx === selectedImageIndex ? '2px solid #1a1a1a' : '2px solid transparent',
                      borderRadius: '6px', cursor: 'pointer', background: 'none', overflow: 'hidden', flexShrink: 0
                    }}
                  >
                    <img src={url} alt={`View ${idx + 1}`} style={{ width: '64px', height: '64px', objectFit: 'cover', display: 'block' }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '32px',
              fontWeight: '500',
              margin: '0 0 20px',
              color: '#1a1a1a'
            }}>
              {product.title}
            </h1>

            {product.subtitle && (
              <p style={{
                fontSize: '16px',
                color: '#666',
                margin: '0 0 15px',
                fontStyle: 'italic'
              }}>
                {product.subtitle}
              </p>
            )}

            {product.description && (
              <p style={{
                fontSize: '15px',
                color: '#444',
                lineHeight: '1.6',
                margin: '0 0 25px'
              }}>
                {product.description}
              </p>
            )}

            {/* Features List */}
            {features.length > 0 && (
              <ul style={{
                listStyle: 'disc',
                paddingLeft: '20px',
                margin: '0 0 30px',
                color: '#555'
              }}>
                {features.map((feature, idx) => (
                  <li key={idx} style={{ 
                    fontSize: '14px', 
                    lineHeight: '1.8',
                    marginBottom: '4px'
                  }}>
                    {feature}
                  </li>
                ))}
              </ul>
            )}

            {/* Price */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '12px',
              marginBottom: '25px'
            }}>
              {onSale && (
                <span style={{
                  background: '#c0392b',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: '700',
                  letterSpacing: '1px',
                  padding: '4px 10px',
                  borderRadius: '4px'
                }}>
                  SALE
                </span>
              )}
              <span style={{ fontSize: '28px', fontWeight: '600', color: onSale ? '#c0392b' : '#1a1a1a' }}>
                ${currentPrice.toFixed(2)}
              </span>
              {onSale && (
                <span style={{ fontSize: '18px', color: '#999', textDecoration: 'line-through' }}>
                  ${getOriginalPrice(selectedSize).toFixed(2)}
                </span>
              )}
              {!onSale && (selectedSize === '2XL' || selectedSize === '3XL') && (
                <span style={{ fontSize: '14px', color: '#888' }}>
                  ({selectedSize} sizing)
                </span>
              )}
            </div>

            {/* Color Selector */}
            {colors.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '10px',
                  color: '#333'
                }}>
                  Color{selectedColor ? <span style={{ fontWeight: '400', color: '#666' }}>: {selectedColor}</span> : ''}
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {colors.map(c => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setSelectedColor(c.name)}
                      title={c.name}
                      aria-label={c.name}
                      aria-pressed={selectedColor === c.name}
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '50%',
                        background: c.hex || '#ccc',
                        border: '1px solid rgba(0,0,0,0.15)',
                        boxShadow: selectedColor === c.name ? '0 0 0 2px #fff, 0 0 0 4px #1a1a1a' : 'none',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'box-shadow 0.15s'
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size Selector */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '10px',
                color: '#333'
              }}>
                Select Size
                {chartSizes.length > 0 && (
                  <a
                    href="#size-chart"
                    onClick={(e) => { e.preventDefault(); document.getElementById('size-chart')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                    style={{ marginLeft: '12px', fontWeight: '400', fontSize: '13px', color: '#666' }}
                  >
                    Sizing chart
                  </a>
                )}
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {sizes.map(size => {
                  const oos = isSizeOutOfStock(size);
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => !oos && setSelectedSize(size)}
                      disabled={oos}
                      style={{
                        padding: '10px 16px',
                        border: selectedSize === size ? '2px solid #1a1a1a' : '1px solid #ccc',
                        borderRadius: '4px',
                        background: oos ? '#f3f4f6' : selectedSize === size ? '#1a1a1a' : '#fff',
                        color: oos ? '#9ca3af' : selectedSize === size ? '#fff' : '#333',
                        cursor: oos ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        minWidth: '55px',
                        transition: 'all 0.15s',
                        textDecoration: oos ? 'line-through' : 'none'
                      }}
                      title={oos ? 'Out of stock' : ''}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                {onSale
                  ? `All sizes on sale: $${product.sale_price.toFixed(2)}`
                  : tieredPricing
                    ? `S-XL: ${basePrice} • 2XL: ${price2xl.toFixed(2)} • 3XL: ${price3xl.toFixed(2)}`
                    : `All sizes: ${basePrice.toFixed(2)}`}
              </p>
            </div>

            {/* Quantity & Add to Cart */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', color: '#666' }}>Qty:</label>
                <select
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  style={{
                    padding: '10px 15px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {qtyOptions.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              {availableForSelectedSize != null && availableForSelectedSize > 0 && availableForSelectedSize <= 5 && (
                <span style={{ fontSize: '13px', color: '#b45309' }}>
                  Only {availableForSelectedSize} left in {selectedSize}
                </span>
              )}

              <Button
                onClick={handleAddToCart}
                disabled={selectedOutOfStock || needsColor}
                style={{
                  padding: '12px 30px',
                  fontSize: '15px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: selectedOutOfStock || needsColor ? '#9ca3af' : '#1a1a1a',
                  border: 'none'
                }}
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingBag size={18} />
                {selectedOutOfStock ? 'Out of Stock' : needsColor ? 'Pick a Color' : 'Add to Cart'}
              </Button>
            </div>

            {/* Sizing Chart */}
            {chartSizes.length > 0 && (
              <div id="size-chart" style={{ marginTop: '10px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '500', margin: '0 0 10px', color: '#1a1a1a' }}>
                  Sizing Chart
                </h3>
                <div style={{ border: '1px solid #1a1a1a', borderRadius: '16px', padding: '10px 16px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', color: '#1a1a1a' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #ddd' }}>
                        <th />
                        {chartSizes.map(size => (
                          <th key={size} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: '700' }}>{size}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chartRows.map((row, idx) => (
                        <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : 'none' }}>
                          <td style={{ padding: '8px 10px 8px 0', whiteSpace: 'nowrap' }}>{row.label}</td>
                          {chartSizes.map(size => (
                            <td key={size} style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{row.values?.[size] || '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: '12px', color: '#888', margin: '6px 0 0' }}>All measurements in inches.</p>
              </div>
            )}

            {/* Back to browse */}
            <Link 
              to={`/shop/tees/${category}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: '#666',
                textDecoration: 'none',
                fontSize: '14px',
                marginTop: '20px'
              }}
            >
              <ArrowLeft size={16} />
              Back to {categoryLabel}
            </Link>

            {/* Shipping Info */}
            <p style={{
              fontSize: '13px',
              color: '#888',
              margin: '30px 0 0',
              paddingTop: '20px',
              borderTop: '1px solid #eee'
            }}>
              Free shipping on orders over $75 • US shipping only
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          [data-testid^="detail-"] {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default TeesProduct;
